import React, { useState } from 'react';
import { getChildFolders } from '../api';

const FOLDER_ICONS = {
  inbox: '📥', sentitems: '📤', drafts: '📝', deleteditems: '🗑️',
  junkemail: '🚫', archive: '📦', outbox: '📮', snoozed: '⏰',
};

function folderIcon(name) {
  return FOLDER_ICONS[name?.toLowerCase().replace(/\s/g, '')] || '📁';
}

function FolderItem({ folder, selectedFolder, onSelect, depth = 0, refreshTick }) {
  const [expanded, setExpanded] = useState(depth === 0 && folder.displayName.toLowerCase() === 'inbox');
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasChildren = folder.childFolderCount > 0;

  async function handleToggle(e) {
    e.stopPropagation();
    if (!expanded && children === null && hasChildren) {
      setLoading(true);
      try {
        const data = await getChildFolders(folder.id);
        setChildren(data);
      } catch (err) {
        setChildren([]);
        console.error('Failed to load children for', folder.displayName, err);
      }
      setLoading(false);
    }
    setExpanded(v => !v);
  }

  React.useEffect(() => {
    if (depth === 0 && folder.displayName.toLowerCase() === 'inbox' && hasChildren && children === null) {
      getChildFolders(folder.id).then(setChildren).catch(() => setChildren([]));
    }
  }, []);

  // Re-fetch children when a refresh is triggered (to update unread counts)
  React.useEffect(() => {
    if (refreshTick && expanded && hasChildren) {
      getChildFolders(folder.id).then(setChildren).catch(() => {});
    }
  }, [refreshTick]);

  const isSelected = selectedFolder?.id === folder.id;

  return (
    <>
      <div
        className={`folder-item ${isSelected ? 'active' : ''}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        onClick={() => onSelect(folder)}
      >
        {hasChildren ? (
          <span className="folder-chevron" onClick={handleToggle}>
            {expanded ? '▾' : '▸'}
          </span>
        ) : (
          <span className="folder-chevron-placeholder" />
        )}
        <span className="folder-icon">{folderIcon(folder.displayName)}</span>
        <span className="folder-name">{folder.displayName}</span>
        {loading && <span className="folder-loading-spinner" />}
        {!loading && folder.unreadItemCount > 0 && (
          <span className="unread-badge">{folder.unreadItemCount}</span>
        )}
      </div>
      {expanded && loading && (
        <div className="folder-loading-row" style={{ paddingLeft: `${28 + depth * 16}px` }}>
          <span className="folder-loading-spinner" /> Loading…
        </div>
      )}
      {expanded && !loading && children && children.map(child => (
        <FolderItem key={child.id} folder={child} selectedFolder={selectedFolder} onSelect={onSelect} depth={depth + 1} refreshTick={refreshTick} />
      ))}
    </>
  );
}

export default function Sidebar({ folders, selectedFolder, onSelect, onCompose, onSettings, onUsers, authUser, onLogout, refreshTick, loadingFolders, onCalendar, showingCalendar }) {
  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-logo">✉</span>
        <span className="sidebar-title">FlexiSupport Mail</span>
      </div>

      <button className="compose-btn" onClick={onCompose}>
        <span>＋</span> New message
      </button>

      <div
        className={`folder-item calendar-nav-item ${showingCalendar ? 'active' : ''}`}
        style={{ paddingLeft: '12px' }}
        onClick={onCalendar}
      >
        <span className="folder-chevron-placeholder" />
        <span className="folder-icon">📅</span>
        <span className="folder-name">Calendar</span>
      </div>

      <nav className="folder-list">
        {loadingFolders ? (
          <div className="folder-loading-initial">
            <div className="folder-loading-spinner" />
            <span>Loading folders…</span>
          </div>
        ) : folders.map(f => (
          <FolderItem key={f.id} folder={f} selectedFolder={selectedFolder} onSelect={onSelect} refreshTick={refreshTick} />
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-name">{authUser?.name}</div>
          <div className="mailbox-address">{authUser?.email}</div>
        </div>
        {onUsers && <button className="settings-btn" onClick={onUsers}>👥 Users</button>}
        <button className="settings-btn" onClick={onSettings}>⚙ Settings</button>
        <button className="settings-btn" onClick={onLogout}>⎋ Sign out</button>
        <button className="settings-btn exit-btn" onClick={() => window.close()}>✕ Exit</button>
      </div>
    </aside>
  );
}
